import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import Log from 'forms-utils-lib/logger/Logger';
import { EMPTY_STATE_TYPE, UI_MODE } from 'framework-listing-lib/constants';
import ListingLibFilters from 'framework-listing-lib/filters/ListingLibFilters';
import ListingLibLayout from 'framework-listing-lib/layout/ListingLibLayout';
import ListingLibTableActions from 'framework-listing-lib/table/ListingLibTableActions';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import DetailsPanel from 'FormsUI/components/dashboard/frameworkTable/DetailsPanel';
import { BUSINESS_UNIT_DROPDOWN_OPTION, COS_INDEX_CONNECTIONS_PARENTS
// @ts-expect-error Missing types
} from 'reference-resolvers/constants/ReferenceObjectTypes';
//@ts-expect-error Missing types
import ProvideReferenceResolvers from 'reference-resolvers/ProvideReferenceResolvers';
//@ts-expect-error Missing types
import BusinessUnitDropdownReferenceResolver from 'reference-resolvers/resolvers/BusinessUnitDropdownReferenceResolver';
import CosIndexConnectionsParentsReferenceResolver from 'reference-resolvers/resolvers/CosIndexConnectionsParentsReferenceResolver';
//@ts-expect-error Missing types
import UserReferenceResolver from 'reference-resolvers/resolvers/UserReferenceResolver';
//@ts-expect-error Missing types
import { USER } from 'reference-resolvers/constants/ReferenceObjectTypes';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import useWindowDimensions from 'forms-utils-lib/hooks/useWindowDimensions';
import { hasBusinessUnitAccess } from 'FormsUI/selectors/Scope';
import useLocalStorage from 'FormsUI/utils/useLocalStorage';
import Filters from 'FormsUI/components/dashboard/frameworkTable/Filters';
import BulkActions from 'FormsUI/components/dashboard/frameworkTable/BulkActions';
import { DEFAULT_COLUMN_KEYS, MAX_HEIGHT_OPTIONS, PROPERTIES, SCROLL_HEIGHT_BREAKPOINT } from 'FormsUI/components/dashboard/frameworkTable/constants';
import CustomColumn from 'FormsUI/components/dashboard/frameworkTable/CustomColumn';
import EmptyStateFolder from 'FormsUI/components/dashboard/frameworkTable/EmptyStateFolder';
import EmptyStateSearch from 'FormsUI/components/dashboard/frameworkTable/EmptyStateSearch';
import FrameworkTableContext from 'FormsUI/components/dashboard/frameworkTable/FrameworkTableContext';
import useRefreshHandler from 'FormsUI/components/dashboard/frameworkTable/hooks/useRefreshHandler';
import PqlRow from 'FormsUI/components/dashboard/frameworkTable/PqlRow';
import ZeroState from 'FormsUI/components/dashboard/frameworkTable/ZeroState';
import FreeStarterEmptyState from 'FormsUI/components/dashboard/zeroState/freeStarter/EmptyState';
import EmptyStateLoading from 'FormsUI/components/dashboard/zeroState/freeStarter/EmptyStateLoading';
import useFormsFrameworkViews from 'FormsUI/components/dashboard/frameworkTable/hooks/useFormsFrameworkViews';
import useFrameworkUIModeProps from 'FormsUI/components/dashboard/frameworkTable/hooks/useFrameworkUIModeProps';
import useIsFormsZeroState from 'FormsUI/components/dashboard/frameworkTable/hooks/useIsFormsZeroState';
import { OpenPopoverProvider } from 'FormsUI/components/dashboard/frameworkTable/OpenPopoverProvider';
import useShowFreeStarterEmptyState from 'FormsUI/components/hooks/zeroState/useShowFreeStarterEmptyState';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const resolvers = {
  [BUSINESS_UNIT_DROPDOWN_OPTION]: BusinessUnitDropdownReferenceResolver,
  [COS_INDEX_CONNECTIONS_PARENTS]: CosIndexConnectionsParentsReferenceResolver,
  [USER]: UserReferenceResolver
};
export const RenderEmptyState = ({
  emptyStateType
}) => {
  const {
    isZeroState: isPortalZeroState,
    loading: isLoadingZeroState
  } = useIsFormsZeroState();
  const showFreeStarterEmptyState = useShowFreeStarterEmptyState(isPortalZeroState);
  if (isLoadingZeroState) {
    return /*#__PURE__*/_jsx(EmptyStateLoading, {});
  }
  if (showFreeStarterEmptyState && !isLoadingZeroState) {
    return /*#__PURE__*/_jsx(FreeStarterEmptyState, {});
  }
  if (emptyStateType === EMPTY_STATE_TYPE.FIRST_TIME_IN_APP) {
    return /*#__PURE__*/_jsx(ZeroState, {});
  } else if (emptyStateType === EMPTY_STATE_TYPE.NO_RESULTS_IN_FOLDER) {
    return /*#__PURE__*/_jsx(EmptyStateFolder, {});
  }
  return /*#__PURE__*/_jsx(EmptyStateSearch, {});
};
const FrameworkTable = () => {
  const businessUnitAccess = useSelector(hasBusinessUnitAccess);
  const [columns, setColumns] = useLocalStorage({
    key: 'forms-dashboard-table/columns'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sidePanelId, setSidePanelId] = useState(0);
  const {
    height
  } = useWindowDimensions();
  const {
    currentUIMode
  } = useFrameworkUIModeProps();
  const {
    isViewsMode
  } = useFormsFrameworkViews();
  useRefreshHandler();
  const modalProps = useMemo(() => {
    const defaultColumnKeys = DEFAULT_COLUMN_KEYS;
    return {
      defaultColumns: defaultColumnKeys,
      loading: isLoading,
      onSave: ({
        columns: newColumns
      }) => {
        setIsLoading(true);
        setColumns(newColumns);
        setIsLoading(false);
        listingLibObserver.closeEditColumnsModal();
      },
      staticColumns: [PROPERTIES.NAME]
    };
  }, [isLoading, setColumns]);
  const columnKeys = useMemo(() => {
    const columnsWithFallback = columns || DEFAULT_COLUMN_KEYS;
    let allColumns = columnsWithFallback;
    //filter business unit column
    allColumns = businessUnitAccess ? allColumns : allColumns.filter(column => column !== PROPERTIES.BUSINESS_UNIT_IDS);
    return allColumns;
  }, [columns, businessUnitAccess]);
  const maxHeight = useMemo(() => height > SCROLL_HEIGHT_BREAKPOINT ? MAX_HEIGHT_OPTIONS.FULL_HEIGHT : MAX_HEIGHT_OPTIONS.AUTO, [height]);
  const rhumbErrorName = useMemo(() => {
    if (currentUIMode === UI_MODE.FOLDERS) {
      return 'FORMS_DASHBOARD_FOLDERS_ERROR';
    }
    if (isViewsMode) {
      return 'FORMS_DASHBOARD_VIEWS_ERROR';
    }
    return 'FORMS_DASHBOARD_MAIN_ERROR';
  }, [currentUIMode, isViewsMode]);
  return /*#__PURE__*/_jsx(FrameworkTableContext.Provider, {
    value: {
      setSidePanelId
    },
    children: /*#__PURE__*/_jsxs(OpenPopoverProvider, {
      children: [/*#__PURE__*/_jsx(UIFloatingAlertList, {}), /*#__PURE__*/_jsx(ListingLibFilters, {
        filterComponent: /*#__PURE__*/_jsx(Filters, {
          hasBusinessUnitAccess: businessUnitAccess
        })
      }), /*#__PURE__*/_jsx(ListingLibLayout, {
        actionsComponent: /*#__PURE__*/_jsx(ListingLibTableActions, {
          editColumnProps: modalProps
        }),
        columnKeys: columnKeys,
        renderEmptyState: RenderEmptyState,
        renderCustomColumn: CustomColumn,
        renderPqlRow: PqlRow,
        rowProps: {
          rowSize: 'small'
        },
        bulkActionProps: {
          canSelectAllAssets: false,
          hasBulkActions: true,
          renderBulkActions: props => /*#__PURE__*/_jsx(BulkActions, Object.assign({}, props))
        },
        renderSuccessMarker: () => /*#__PURE__*/_jsx(NavMarker, {
          name: "FORMS_DASHBOARD_TABLE_LOAD"
        }),
        renderErrorMarker: error => {
          const statusCode = error && error.networkError && error.networkError.status;
          switch (statusCode) {
            case 0:
              return null;
            default:
              Log.error('framework table failed to load', {
                error: error ? error.message : 'unknown error'
              });
              return /*#__PURE__*/_jsx(NavMarker, {
                name: rhumbErrorName
              });
          }
        },
        "data-test-id": "frameworkTable",
        maxHeight: maxHeight
      }), sidePanelId ? /*#__PURE__*/_jsx(DetailsPanel, {
        objectId: sidePanelId,
        setId: setSidePanelId
      }) : null]
    })
  });
};
export default ProvideReferenceResolvers(resolvers)(FrameworkTable);